import {AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormControl} from "@angular/forms";
import {ICLTableButton, ICLTableState, MapDisplayColumns, MappedColumns} from "@clavisco/table";
import {
  CL_CHANNEL,
  CL_DISPLAY,
  ICLCallbacksInterface,
  ICLEvent,
  LinkerService,
  Register,
  Run,
  StepDown
} from "@clavisco/linker";
import {debounceTime, filter, Subscription} from "rxjs";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {DOCUMENT} from "@angular/common";
import {CLPrint, Repository, Structures} from "@clavisco/core";
import CL_ACTIONS = Structures.Enums.CL_ACTIONS;
import {IRequestRecordsEventData, ISearchModalComponentDialogData} from "@app/interfaces/i-local-search-modal";

@Component({
  selector: 'app-local-search-modal',
  templateUrl: './local-search-modal.component.html',
  styleUrls: ['./local-search-modal.component.scss']
})
export class LocalSearchModalComponent<TRecord> implements OnInit, AfterViewInit, OnDestroy  {


  searchInputControl = new FormControl('');

  tableId = 'ClMasterDataTableId';

  tableMappedColumns: MappedColumns;

  tableRecords: TRecord[];

  tableRecordsCount: number = 0;

  modalTitle!: string;

  tableScrollHeight: string = 'auto';

  tableShouldPaginateRequest: boolean;

  tableButtons = [] as ICLTableButton[];

  itemsPerPage: number = 10;

  callbacks: ICLCallbacksInterface<CL_CHANNEL> = {
    Callbacks: {},
    Tracks: [],
  }

  subscriptions$: Subscription = new Subscription();

  constructor(@Inject(MAT_DIALOG_DATA) private matDialogData: ISearchModalComponentDialogData<TRecord>,
              @Inject('LinkerService') private linkerService: LinkerService,
              private matDialogRef: MatDialogRef<LocalSearchModalComponent<TRecord>>,
              @Inject(DOCUMENT) private document: Document,
              private changeDetectorRef: ChangeDetectorRef) {
    this.modalTitle = this.matDialogData.ModalTitle || '';

    this.tableShouldPaginateRequest = this.matDialogData.ShouldPaginateRequest || false;

    this.matDialogData.InputDebounceTime = this.matDialogData.InputDebounceTime || 200;

    this.matDialogData.MinInputCharacters = this.matDialogData.MinInputCharacters || 4;

    this.tableRecords = this.matDialogData.Records || [];

    this.tableButtons.push(
      {
        Action: CL_ACTIONS.OPTION_1,
        Icon: this.matDialogData.SelectRowIcon ?? 'arrow_forward_ios',
        Title: 'Seleccionar'
      } as ICLTableButton)

    this.tableRecordsCount = this.matDialogData.RecordsCount || 0;

    this.tableMappedColumns = MapDisplayColumns({
      dataSource: this.tableRecords,
      renameColumns: this.matDialogData.TableMappedColumns?.RenameColumns || undefined,
      ignoreColumns: this.matDialogData.TableMappedColumns?.IgnoreColumns || [],
      stickyColumns: [
        {Name: 'Options', FixOn: 'right'}
      ]
    });
  }

  ngOnInit(): void {
    this.InitialValidations();
    // Use to listen event
    this.subscriptions$.add(this.linkerService.Flow()?.pipe(StepDown(this.callbacks)).subscribe({
      next: callback => Run(callback.Target, callback, this.callbacks.Callbacks),
      error: error => CLPrint(error)
    }));

    this.RegisterComponentListenEvents();
    this.ListenSearchInputValueChange();
  }

  ngAfterViewInit() {
    if(this.tableRecords && this.tableRecords.length)
    {
      setTimeout(() => {
        this.InflateTable();
        this.SetTableScrollHeight();
      }, 0)
    }
    else
    {
      this.linkerService.Publish({
        CallBack: CL_CHANNEL.PRE_REQ_RECORDS,
        Target: this.tableId,
        Data: ''
      });
      this.SetTableScrollHeight();
    }
  }

  /**
   * Insert the current records to the table
   * @constructor
   */
  InflateTable(): void
  {
    const NEW_TABLE_STATE = {
      Records: this.tableRecords,
      RecordsCount: this.tableRecordsCount
    }

    this.linkerService.Publish({
      Target: this.tableId,
      Data: JSON.stringify(NEW_TABLE_STATE),
      CallBack: CL_CHANNEL.INFLATE
    });

    setTimeout(() => {
      this.SetTableScrollHeight();
    }, 0);
  }

  /**
   * Calculate the table scroll height to avoid overflows in the modal
   * @constructor
   */
  SetTableScrollHeight(): void
  {
    let modalBodyTableContainer = this.document.querySelector(".modal-main_container-body-table_container")!;
    let tableContainerHeight = +window.getComputedStyle(modalBodyTableContainer).height.replace('px', '');

    let modalBodySearchBar = this.document.querySelector(".modal-main_container-body-search_bar")!;
    let searchBarHeight = +window.getComputedStyle(modalBodySearchBar).height.replace("px", "");

    let modalHeader = this.document.querySelector(".modal-main_container-header")!;
    let headerHeight = +window.getComputedStyle(modalHeader).height.replace('px', '') + +window.getComputedStyle(modalHeader).paddingBottom.replace('px', '');

    let modal = this.matDialogRef._containerInstance['_elementRef'].nativeElement;
    let modalHeight = +window.getComputedStyle(modal).height.replace('px', '');
    let modalPaddings =  +window.getComputedStyle(modal).paddingTop.replace('px', '') + +window.getComputedStyle(modal).paddingBottom.replace('px', '');

    let hasScroll = (tableContainerHeight + searchBarHeight + headerHeight + modalPaddings) > modalHeight;

    let resultHeight = ((tableContainerHeight - Math.abs((modalHeight - searchBarHeight - headerHeight) - tableContainerHeight)) - searchBarHeight) - headerHeight;

    this.tableScrollHeight = hasScroll ? `calc(110vh - ${resultHeight}px)` : 'auto';

    this.changeDetectorRef.detectChanges();
  }

  /**
   * Process to run when the table page change
   * @param _event Returned data from table
   * @constructor
   */
  OnTablePageChange = (_event: ICLEvent) => {
    let eventData = JSON.parse(_event.Data);

    this.RequestRecords(eventData.SLStart, eventData.SLEnd);
  }

  /**
   * Process to run when the table emit the request records event
   * @param _event Returned data from table
   * @constructor
   */
  OnTableRequestRecords = (_event: ICLEvent) => {
    this.RequestRecords();
  }

  /**
   * Process to run when table button is clicked
   * @param _event Returned data from table. Contains the row of the button clicked
   * @constructor
   */
  OnTableButtonClicked = (_event: ICLEvent) => {
    let eventData = JSON.parse(_event.Data) as ICLTableButton;

    switch(eventData.Action)
    {
      // Select button
      case Structures.Enums.CL_ACTIONS.OPTION_1:
        this.linkerService.Publish({
          CallBack: CL_CHANNEL.OUTPUT,
          Data: eventData.Data!,
          Target: this.matDialogData.Id
        });
        this.matDialogRef.close()
    }
  }

  /**
   * Emit an event to indicate that the user is searching a record or if the table page change to update the records in the table
   * @constructor
   */
  RequestRecords(_skipRecords: number = -1, _takeRecords: number = -1): void
  {
    let searchValue = this.searchInputControl.value;

    let requestRecordsEventData = {
      SearchValue: searchValue,
      SkipRecords: _skipRecords,
      TakeRecords: _takeRecords
    } as IRequestRecordsEventData;

    this.linkerService.Publish({
      Target: this.matDialogData.Id,
      Data: JSON.stringify(requestRecordsEventData),
      CallBack: CL_CHANNEL.REQUEST_RECORDS
    });
  }

  /**
   * Process to listen when the app is updating the records of the table
   * @param _event Incoming data from host application
   * @constructor
   */
  OnInflateModal = (_event: ICLEvent) => {
    let eventData = JSON.parse(_event.Data) as ICLTableState<any>;

    // Used setTimeout to avoid ExpressionChangedAfterItHasBeenChecked error
    setTimeout(() => {
      this.tableRecords = eventData.Records || [];

      this.tableRecordsCount = eventData.RecordsCount || 0;

      this.InflateTable();
    }, 0);
  }

  /**
   * Register all events listened by this component
   * @constructor
   */
  RegisterComponentListenEvents(): void
  {
    // Table events
    if(!this.matDialogData.ShouldPaginateRequest) Register<CL_CHANNEL>(this.tableId, CL_CHANNEL.INPUT, this.OnTablePageChange, this.callbacks);
    Register<CL_CHANNEL>(this.tableId, CL_CHANNEL.OUTPUT, this.OnTableButtonClicked, this.callbacks);
    if(this.matDialogData.ShouldPaginateRequest) Register<CL_CHANNEL>(this.tableId, CL_CHANNEL.REQUEST_RECORDS, this.OnTableRequestRecords, this.callbacks);

    // Current modal events
    Register<CL_CHANNEL>(this.matDialogData.Id, CL_CHANNEL.INFLATE, this.OnInflateModal, this.callbacks);
  }

  /**
   * Listen the changes in the value of search input and emit an event by linkerService
   * @constructor
   */
  ListenSearchInputValueChange(): void
  {
    this.searchInputControl.valueChanges
      .pipe(
        filter(value => value.length >= this.matDialogData.MinInputCharacters! || !value),
        debounceTime(this.matDialogData.InputDebounceTime!)
      )
      .subscribe({
        next: (value) => {
          if(this.matDialogData.ShouldPaginateRequest)
          {
            this.linkerService.Publish({
              CallBack: CL_CHANNEL.PRE_REQ_RECORDS,
              Target: this.tableId,
              Data: ''
            });
          }
          else
          {
            this.RequestRecords();
          }
        }
      });
  }

  /**
   * Validates that component has all required data
   * @constructor
   */
  InitialValidations(): void
  {
    if(!this.matDialogData.Id) CLPrint("You must supply the modal ID", CL_DISPLAY.ERROR);
  }

  ngOnDestroy() {
    this.subscriptions$.unsubscribe();
  }
}
